import React, { useState, useCallback } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import siteData from "../siteData";

const fadeUp = {
  hidden: { opacity: 0, y: 40, filter: "blur(8px)" },
  visible: {
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
    transition: { duration: 0.8, ease: [0.19, 1, 0.22, 1] },
  },
};

const stagger = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.15 },
  },
};

const vp = { once: true, amount: 0.15 };

export default function ContactUs() {
  const [formData, setFormData] = useState({
    name: "",
    subject: "",
    email: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");

  const handleChange = useCallback(
    (e) => {
      const { name, value } = e.target;
      setFormData((prev) => ({ ...prev, [name]: value }));
      if (submitStatus) setSubmitStatus(null);
    },
    [submitStatus],
  );

  const handleSubmit = useCallback(
    async (event) => {
      event.preventDefault();
      setIsSubmitting(true);
      setSubmitStatus(null);
      setErrorMessage("");

      try {
        const response = await axios.post(siteData.contactApiUrl, formData);
        console.log("Form submitted successfully:", response.data);
        setSubmitStatus("success");
        setFormData({ name: "", subject: "", email: "", message: "" });
      } catch (error) {
        console.error("Form submission error:", error);
        setSubmitStatus("error");
        setErrorMessage(
          error.response?.data?.message ||
            "Failed to send message. Please try again or email me directly.",
        );
      } finally {
        setIsSubmitting(false);
      }
    },
    [formData],
  );

  return (
    <section className="contact section" id="contact">
      <motion.div
        className="container"
        initial="hidden"
        whileInView="visible"
        viewport={vp}
        variants={stagger}
      >
        {/* Watermark */}
        <div className="section-watermark">CONTACT</div>

        <motion.div className="contact-layout" variants={fadeUp}>
          {/* Left - Big text + details */}
          <div className="contact-left">
            <motion.h2 variants={fadeUp}>
              Let's
              <br />
              <span className="gradient-text">Talk.</span>
            </motion.h2>
            <motion.p variants={fadeUp}>
              Have a project in mind or want to chat? I'd love to hear from you.
            </motion.p>

            <motion.div variants={stagger}>
              <div className="contact-detail">
                <div className="icon-box">
                  <i className="fa fa-map-marker" />
                </div>
                <div className="detail-text">
                  <div className="detail-label">Location</div>
                  <div className="detail-value">{siteData.locationFull}</div>
                </div>
              </div>
              <div className="contact-detail">
                <div className="icon-box">
                  <i className="fa fa-envelope" />
                </div>
                <div className="detail-text">
                  <div className="detail-label">Email</div>
                  <div className="detail-value">
                    <a href={`mailto:${siteData.email}`}>{siteData.email}</a>
                  </div>
                </div>
              </div>
              <div className="contact-detail">
                <div className="icon-box">
                  <i className="fa fa-phone" />
                </div>
                <div className="detail-text">
                  <div className="detail-label">Phone</div>
                  <div className="detail-value">{siteData.phone}</div>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Right - Form */}
          <motion.form
            className="contact-form"
            onSubmit={handleSubmit}
            id="contactForm"
            variants={fadeUp}
          >
            {submitStatus === "success" && (
              <div className="form-status success">
                Message sent successfully! I'll get back to you soon.
              </div>
            )}
            {submitStatus === "error" && (
              <div className="form-status error">{errorMessage}</div>
            )}

            <div className="form-group">
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Your Name"
                required
                disabled={isSubmitting}
              />
            </div>
            <div className="form-group">
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Your Email"
                required
                disabled={isSubmitting}
              />
            </div>
            <div className="form-group">
              <input
                type="text"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                placeholder="Subject"
                required
                disabled={isSubmitting}
              />
            </div>
            <div className="form-group">
              <textarea
                name="message"
                rows="6"
                value={formData.message}
                onChange={handleChange}
                placeholder="Your Message"
                required
                disabled={isSubmitting}
              />
            </div>
            <motion.button
              type="submit"
              className="btn btn-primary"
              disabled={isSubmitting}
              whileHover={{
                scale: 1.01,
                boxShadow: "0 0 50px rgba(0,230,138,0.25)",
              }}
              whileTap={{ scale: 0.98 }}
              style={{
                opacity: isSubmitting ? 0.5 : 1,
                width: "100%",
                justifyContent: "center",
              }}
            >
              {isSubmitting ? "Sending..." : "Send Message"}
            </motion.button>
          </motion.form>
        </motion.div>
      </motion.div>
    </section>
  );
}
